/* eslint-disable lyrahealth/no-react-native-text */
import React from 'react'

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { addDays, format, parseISO } from 'date-fns'
import { isEmpty, isInteger, isNil, isNumber } from 'lodash-es'

import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { getCHRTHighRiskDetails } from '@lyrahealth-inc/shared-app-logic/src/features/highAlert/utils'
import { AssessmentType } from '@lyrahealth-inc/shared-app-logic/src/models/assessment/Assessment'
import { Outcome, Scores } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { outcomeChartConstants } from '@lyrahealth-inc/ui-core/lib/constants/outcomeConstants'
import {
  bingeDrinkingDays,
  timelineFollowbackMethodData,
  timelineFollowbackMethodDataOtherSubstances,
} from '@lyrahealth-inc/ui-core/lib/molecules/outcomeChart/OutcomeChart'
import outcomeUtils from '@lyrahealth-inc/ui-core/lib/utils/outcomeUtils'

type ClientProgressPDFProps = {
  outcomes: Outcome[]
  pdfContentStartDate?: string
  pdfContentEndDate?: string
  providerEpisodesInfo?: { text: string; id: string }[]
}

const styles = StyleSheet.create({
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: lightThemeColors.textActive,
    marginBottom: 20,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textActive,
  },
  episodeTitle: {
    fontSize: 18,
    color: lightThemeColors.textSecondary,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textSecondary,
    marginBottom: 20,
    paddingBottom: 5,
  },
  subheader: {
    fontSize: 16,
    color: lightThemeColors.textPrimary,
    marginBottom: 12,
  },
  chartSubheaderDescription: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    marginBottom: 12,
  },
  outcomeChartsContainer: {
    marginBottom: 20,
  },
  tableContainer: {
    marginBottom: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: lightThemeColors.backgroundElement,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.borderDefault,
    padding: 8,
  },
  tableHeaderCell: {
    fontWeight: 'bold',
    flex: 1,
    fontSize: 11,
    color: lightThemeColors.textPrimary,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.borderDefault,
    padding: 12,
  },
  tableCell: {
    flex: 1,
    textAlign: 'left',
  },
  tableCellContents: {
    flexDirection: 'column',
    gap: 8,
  },
  tableCellValue: {
    fontSize: 14,
    color: lightThemeColors.textPrimary,
  },
  tableCellSubcategory: {
    fontSize: 9,
    color: lightThemeColors.textSecondary,
  },
  tableCellErrorText: {
    fontSize: 9,
    color: lightThemeColors.textError,
  },
  answerSection: {
    marginBottom: 12,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
  },
  answerSectionTitle: {
    fontWeight: 'bold',
    fontSize: 14,
    color: lightThemeColors.textPrimary,
    marginBottom: 8,
  },
  answerDescription: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    marginBottom: 8,
  },
  questionAnswer: {
    marginBottom: 14,
  },
  questionText: {
    fontSize: 12,
    color: lightThemeColors.textPrimary,
    marginBottom: 10,
  },
  answerText: {
    fontSize: 12,
    color: lightThemeColors.textPrimary,
    paddingLeft: 20,
  },
  scoreKey: {
    backgroundColor: lightThemeColors.backgroundElement,
    padding: 24,
    marginBottom: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  scoreKeyColumn: {
    flex: 1,
  },
  scoreKeyLeftColumn: {
    borderRightWidth: 1,
    borderRightColor: lightThemeColors.borderDefault,
  },
  scoreKeyRightColumn: {
    paddingLeft: 24,
  },
  scoreKeyTitle: {
    fontWeight: 'bold',
    fontSize: 12,
    color: lightThemeColors.textPrimary,
    marginBottom: 12,
  },
  scoreKeyItem: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'flex-start',
  },
  scoreKeyValue: {
    fontWeight: 'bold',
    fontSize: 10,
    color: lightThemeColors.textPrimary,
    width: 30,
    marginRight: 8,
  },
  scoreKeyLabel: {
    fontSize: 10,
    color: lightThemeColors.textPrimary,
    flex: 1,
  },
  emptyMessage: {
    fontSize: 12,
    color: lightThemeColors.textSecondary,
    textAlign: 'center',
    marginBottom: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
  },
})

const assessmentConfigs = [
  { key: 'CGSQ', displayName: 'CGSQ', checkFunction: isNumber },
  { key: 'PARENTAL-STRESS', displayName: 'PARENTAL STRESS', checkFunction: isInteger },
  { key: 'PARENTING-TASK', displayName: 'PARENTING TASK', checkFunction: isNumber },
  { key: 'RCADS-ANXIETY', displayName: 'RCADS: Anxiety', checkFunction: isInteger },
  { key: 'RCADS-DEPRESSION', displayName: 'RCADS: Depression', checkFunction: isInteger },
  { key: 'RCADS-TOTAL', displayName: 'RCADS: Total', checkFunction: isInteger },
  { key: 'TFM', displayName: 'TFM', checkFunction: isInteger },
  { key: 'TLFB-ALCOHOL', displayName: 'TLFB Alcohol', checkFunction: isInteger },
  { key: 'TLFB-OTHER-SUBSTANCES', displayName: 'TLFB Substances', checkFunction: isInteger },
  { key: 'BDD', displayName: 'BDD', checkFunction: isInteger },
  { key: 'PACS', displayName: 'PACS', checkFunction: isInteger },
  { key: 'ADCS', displayName: 'ADCS', checkFunction: isInteger },
  { key: 'WSAS', displayName: 'WSAS', checkFunction: isInteger },
  { key: 'PCL5', displayName: 'PCL5', checkFunction: isInteger },
  { key: 'CHRT', displayName: 'CHRT', checkFunction: isInteger },
  { key: 'GAD', displayName: 'GAD7', checkFunction: isInteger },
  { key: 'PHQ', displayName: 'PHQ9', checkFunction: isInteger },
  { key: 'PSS', displayName: 'PSS', checkFunction: isInteger },
  { key: 'WELL-BEING', displayName: 'Well Being', checkFunction: isInteger },
  { key: 'FTND', displayName: 'FTND', checkFunction: isInteger },
  { key: 'E-FTND', displayName: 'E-FTND', checkFunction: isInteger },
]

const ClientProgressPDF = ({
  outcomes,
  pdfContentStartDate,
  pdfContentEndDate,
  providerEpisodesInfo,
}: ClientProgressPDFProps):
  | {
      episodeName: string
      progressByEpisode: React.ReactNode
    }[]
  | undefined => {
  const filterOutcomesByDateRange = (outcomesToFilter: Outcome[]) => {
    return outcomesToFilter.filter((outcome) => {
      if (!outcome.response_date) {
        return false
      }
      const outcomeDate = new Date(outcome.response_date)
      return (
        (pdfContentStartDate ? outcomeDate >= new Date(pdfContentStartDate) : true) &&
        (pdfContentEndDate ? outcomeDate <= addDays(new Date(pdfContentEndDate), 1) : true)
      )
    })
  }

  const outcomesInDateRange: Outcome[] = filterOutcomesByDateRange(outcomes)

  if (outcomesInDateRange.length === 0) {
    return undefined
  }

  const renderProgressTables = (episodeOutcomes: Outcome[]) => {
    if (episodeOutcomes.length === 0) {
      return null
    }

    return episodeOutcomes.map((outcome, outcomeIndex) => {
      const outcomeAssessments = assessmentConfigs.filter((config) =>
        config.checkFunction((outcome.scores as any)[config.key]),
      )

      const outcomeHasAllianceScores =
        isInteger(outcome.scores['SATISFACTION-GOALS']) ||
        isInteger(outcome.scores['SATISFACTION-RELATIONSHIP']) ||
        isInteger(outcome.scores['SATISFACTION-THERAPY'])

      const outcomeHasLeapsScores = !isNil(outcome.scores.LEAPS)

      const leapsNwImpScore = outcome.scores['LEAPS-NW-IMP']
      const leapsPrdSsScore = outcome.scores['LEAPS-PRD-SS']
      const hasNwImp = isInteger(leapsNwImpScore)
      const hasPrdSs = isInteger(leapsPrdSsScore)

      if (outcomeAssessments.length === 0 && !outcomeHasAllianceScores && !outcomeHasLeapsScores) {
        return null
      }

      return (
        <View key={outcomeIndex}>
          <Text style={styles.subheader}>{format(parseISO(outcome.response_date), 'MM/dd/yyyy')}</Text>

          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableHeaderCell}>Assessment</Text>
              <Text style={styles.tableHeaderCell}>Score</Text>
              <Text style={styles.tableHeaderCell}>Description</Text>
            </View>

            {outcomeAssessments.map((config, index) => {
              const score = (outcome.scores as any)[config.key]
              const description = outcomeUtils.convertOutcomeScoreToDescription(config.key, score)
              const isLastRow =
                index === outcomeAssessments.length - 1 && !(outcomeHasAllianceScores || outcomeHasLeapsScores)

              if (config.key === 'CHRT') {
                const chrtHighRiskDetails = getCHRTHighRiskDetails(outcome.response_details)

                return (
                  <View key={config.key} style={[styles.tableRow, ...(isLastRow ? [{ borderBottomWidth: 0 }] : [])]}>
                    <View style={styles.tableCell}>
                      <View style={styles.tableCellContents}>
                        <Text style={styles.tableCellValue}>{config.displayName}</Text>
                        {chrtHighRiskDetails.map((detail) => (
                          <Text key={detail['question-number']} style={styles.tableCellErrorText}>
                            {detail.question}
                          </Text>
                        ))}
                      </View>
                    </View>
                    <View style={styles.tableCell}>
                      <View style={styles.tableCellContents}>
                        <Text style={styles.tableCellValue}>{score}</Text>
                      </View>
                    </View>
                    <View style={styles.tableCell}>
                      <View style={styles.tableCellContents}>
                        <Text style={styles.tableCellValue}>{description ?? ''}</Text>
                        {chrtHighRiskDetails.map((detail) => (
                          <Text key={detail['question-number']} style={styles.tableCellErrorText}>
                            {detail['answer-text']}
                          </Text>
                        ))}
                      </View>
                    </View>
                  </View>
                )
              }

              if (config.key === 'PHQ') {
                const phqLifeScore = outcome.scores['PHQ-LIFE']
                const phqSelfHarmScore = outcome.scores['PHQ-SELF-HARM']
                const hasLifeImpairment = isInteger(phqLifeScore)
                const hasSelfHarm = isInteger(phqSelfHarmScore)

                return (
                  <View key={config.key} style={[styles.tableRow, ...(isLastRow ? [{ borderBottomWidth: 0 }] : [])]}>
                    <View style={styles.tableCell}>
                      <View style={styles.tableCellContents}>
                        <Text style={styles.tableCellValue}>{config.displayName}</Text>
                        {hasLifeImpairment && <Text style={styles.tableCellSubcategory}>Life Impairment Question</Text>}
                        {hasSelfHarm && (
                          <Text style={styles.tableCellErrorText}>Thoughts of self harm and/or suicide</Text>
                        )}
                      </View>
                    </View>
                    <View style={styles.tableCell}>
                      <View style={styles.tableCellContents}>
                        <Text style={styles.tableCellValue}>{score}</Text>
                      </View>
                    </View>
                    <View style={styles.tableCell}>
                      <View style={styles.tableCellContents}>
                        <Text style={styles.tableCellValue}>{description ?? ''}</Text>
                        {hasLifeImpairment && (
                          <Text style={styles.tableCellSubcategory}>
                            {outcomeUtils.convertOutcomeScoreToDescription('PHQ-LIFE', phqLifeScore)}
                          </Text>
                        )}
                        {hasSelfHarm && (
                          <Text style={styles.tableCellErrorText}>
                            {outcomeUtils.convertOutcomeScoreToDescription('PHQ-SELF-HARM', phqSelfHarmScore)}
                          </Text>
                        )}
                      </View>
                    </View>
                  </View>
                )
              }

              return (
                <View key={config.key} style={[styles.tableRow, ...(isLastRow ? [{ borderBottomWidth: 0 }] : [])]}>
                  <View style={styles.tableCell}>
                    <View style={styles.tableCellContents}>
                      <Text style={styles.tableCellValue}>{config.displayName}</Text>
                    </View>
                  </View>
                  <View style={styles.tableCell}>
                    <View style={styles.tableCellContents}>
                      <Text style={styles.tableCellValue}>{score}</Text>
                    </View>
                  </View>
                  <View style={styles.tableCell}>
                    <View style={styles.tableCellContents}>
                      <Text style={styles.tableCellValue}>{description ?? ''}</Text>
                    </View>
                  </View>
                </View>
              )
            })}

            {outcomeHasAllianceScores && (
              <View style={[styles.tableRow, ...(!outcomeHasLeapsScores ? [{ borderBottomWidth: 0 }] : [])]}>
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue}>Alliance Questions</Text>
                    {isInteger(outcome.scores['SATISFACTION-GOALS']) && (
                      <Text style={styles.tableCellSubcategory}>Therapy Approach Satisfaction</Text>
                    )}
                    {isInteger(outcome.scores['SATISFACTION-RELATIONSHIP']) && (
                      <Text style={styles.tableCellSubcategory}>Relationship Satisfaction</Text>
                    )}
                    {isInteger(outcome.scores['SATISFACTION-THERAPY']) && (
                      <Text style={styles.tableCellSubcategory}>Therapy Content Satisfaction</Text>
                    )}
                  </View>
                </View>
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents} />
                </View>
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue} />
                    {isInteger(outcome.scores['SATISFACTION-GOALS']) && (
                      <Text style={styles.tableCellSubcategory}>
                        {outcomeUtils.convertOutcomeScoreToDescription(
                          'SATISFACTION',
                          outcome.scores['SATISFACTION-GOALS'],
                        )}
                      </Text>
                    )}
                    {isInteger(outcome.scores['SATISFACTION-RELATIONSHIP']) && (
                      <Text style={styles.tableCellSubcategory}>
                        {outcomeUtils.convertOutcomeScoreToDescription(
                          'SATISFACTION',
                          outcome.scores['SATISFACTION-RELATIONSHIP'],
                        )}
                      </Text>
                    )}
                    {isInteger(outcome.scores['SATISFACTION-THERAPY']) && (
                      <Text style={styles.tableCellSubcategory}>
                        {outcomeUtils.convertOutcomeScoreToDescription(
                          'SATISFACTION',
                          outcome.scores['SATISFACTION-THERAPY'],
                        )}
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            )}

            {outcomeHasLeapsScores && (
              <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue}>LEAPS</Text>
                    {hasNwImp && <Text style={styles.tableCellSubcategory}>Percent Non-work Impairment</Text>}
                    {hasPrdSs && <Text style={styles.tableCellSubcategory}>Productivity Subscale</Text>}
                  </View>
                </View>
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue}>{outcome.scores.LEAPS}</Text>
                  </View>
                </View>
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue}>
                      {outcomeUtils.convertOutcomeScoreToDescription('LEAPS', outcome.scores.LEAPS)}
                    </Text>
                    {hasNwImp && <Text style={styles.tableCellSubcategory}>{leapsNwImpScore}%</Text>}
                    {hasPrdSs && <Text style={styles.tableCellSubcategory}>{leapsPrdSsScore}</Text>}
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>
      )
    })
  }

  const renderOutcomeCharts = (episodeOutcomes: Outcome[]) => {
    if (episodeOutcomes.length === 0) {
      return null
    }

    const sortOutcomesByDate = (data: Outcome[]): Outcome[] => {
      return data.sort((a, b) => new Date(b.response_date).getTime() - new Date(a.response_date).getTime())
    }

    return (
      <View style={styles.outcomeChartsContainer}>
        {outcomeChartConstants.map((assessment) => {
          const assessmentType = assessment.name
          let data: Outcome[] = episodeOutcomes.filter((outcome) =>
            isInteger(outcome.scores[assessmentType as keyof Scores]),
          )

          if (assessmentType === AssessmentType.TFM && data) data = timelineFollowbackMethodData(data)
          else if (assessmentType === AssessmentType.TLFB_ALCOHOL) data = timelineFollowbackMethodData(data)
          else if (assessmentType === AssessmentType.TLFB_OTHER_SUBSTANCES)
            data = timelineFollowbackMethodDataOtherSubstances({
              data,
              groupName: AssessmentType.TLFB_OTHER_SUBSTANCES,
              assessment,
            })
          else if (assessmentType === AssessmentType.BDD && data.length > 0) data = bingeDrinkingDays(data)
          else if (assessmentType === AssessmentType.CGSQ || assessmentType === AssessmentType.PARENTING_TASK) {
            data = outcomesInDateRange.filter((outcome) => isNumber(outcome.scores[assessmentType]))
            data = sortOutcomesByDate(data)
          } else {
            data = sortOutcomesByDate(data)
          }
          if (isEmpty(data)) return null

          return (
            <View key={assessment.name} style={{ marginBottom: 20 }}>
              <Text style={styles.subheader}>{assessment.title}</Text>
              <Text style={styles.chartSubheaderDescription}>{assessment.description}</Text>
              {renderOutcomeTable({ assessmentType, assessmentCondition: assessment.condition, outcomeData: data })}
              {renderAnswerSections({ assessmentType: assessment.name, outcomeData: data, assessment })}
            </View>
          )
        })}
      </View>
    )
  }

  const renderOutcomeTable = ({
    assessmentType,
    assessmentCondition,
    outcomeData,
  }: {
    assessmentType: string
    assessmentCondition?: string
    outcomeData: Outcome[]
  }) => {
    const showSelfHarm =
      assessmentType === AssessmentType.PHQ && outcomeData.some((outcome) => isInteger(outcome.scores['PHQ-SELF-HARM']))

    const showCHRTHighRisk =
      assessmentType.includes(AssessmentType.CHRT) &&
      outcomeData.some((outcome) => getCHRTHighRiskDetails(outcome.response_details).length > 0)

    const showScoreDescription = outcomeData.some((dataEntry) => {
      return !['No score', ''].includes(
        outcomeUtils
          .convertOutcomeScoreToDescription(
            assessmentType,
            assessmentType === AssessmentType.TFM
              ? dataEntry.scores.actualScore
              : dataEntry.scores[assessmentType as keyof Scores],
          )
          .replace(assessmentCondition ?? '', ''),
      )
    })
    return (
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <Text
            style={[
              styles.tableHeaderCell,
              showScoreDescription || showSelfHarm || showCHRTHighRisk ? { width: '100px' } : {},
            ]}
          >
            Date
          </Text>
          <Text style={[styles.tableHeaderCell, { width: '20px' }]}>Score</Text>
          {showScoreDescription && <Text style={styles.tableHeaderCell}>Description</Text>}
          {showSelfHarm && <Text style={styles.tableHeaderCell}>Self Harm</Text>}
          {showCHRTHighRisk && <Text style={styles.tableHeaderCell}>High Risk</Text>}
        </View>

        {outcomeData.map((dataEntry, index) => {
          let score = dataEntry.scores[assessmentType as keyof Scores]
          if (isNil(score)) return null

          let selfHarm = false
          const chrtHighRiskDetails = assessmentType.includes(AssessmentType.CHRT)
            ? getCHRTHighRiskDetails(dataEntry.response_details)
            : []
          if (assessmentType === AssessmentType.TFM || assessmentType === AssessmentType.TLFB_ALCOHOL) {
            score = dataEntry.scores.actualScore
          }

          const scoreDescription = outcomeUtils
            .convertOutcomeScoreToDescription(assessmentType, score)
            .replace(assessmentCondition ?? '', '')

          if (
            assessmentType === 'PHQ' &&
            'PHQ-SELF-HARM' in dataEntry.scores &&
            dataEntry.scores['PHQ-SELF-HARM'] &&
            dataEntry.scores['PHQ-SELF-HARM'] > 0
          ) {
            selfHarm = true
          }
          const isLastRow = index === outcomeData.length - 1

          return (
            <View key={index} style={[styles.tableRow, ...(isLastRow ? [{ borderBottomWidth: 0 }] : [])]}>
              <View
                style={[
                  styles.tableCell,
                  showScoreDescription || showSelfHarm || showCHRTHighRisk ? { width: '100px' } : {},
                ]}
              >
                <View style={styles.tableCellContents}>
                  <Text style={styles.tableCellValue}>{format(new Date(dataEntry.response_date), 'MM/dd/yyyy')}</Text>
                </View>
              </View>
              <View style={[styles.tableCell, { width: '20px' }]}>
                <View style={styles.tableCellContents}>
                  <Text style={styles.tableCellValue}>{score}</Text>
                </View>
              </View>
              {showScoreDescription && (
                <View style={styles.tableCell}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue}>{scoreDescription}</Text>
                  </View>
                </View>
              )}
              {showSelfHarm && (
                <View style={[styles.tableCell, { width: '500px' }]}>
                  <View style={styles.tableCellContents}>
                    <Text style={styles.tableCellValue}>{selfHarm ? 'Thoughts of self harm or suicide' : ''}</Text>
                  </View>
                </View>
              )}
              {showCHRTHighRisk && (
                <View style={[styles.tableCell, { width: '500px' }]}>
                  <View style={styles.tableCellContents}>
                    {chrtHighRiskDetails.map((detail) => (
                      <Text key={detail['question-number']} style={styles.tableCellErrorText}>
                        {detail.question}: {detail['answer-text']}
                      </Text>
                    ))}
                  </View>
                </View>
              )}
            </View>
          )
        })}
      </View>
    )
  }

  const renderAnswerSections = ({
    assessmentType,
    outcomeData,
    assessment,
  }: {
    assessmentType: string
    outcomeData: Outcome[]
    assessment: any
  }) => {
    return outcomeData.map((outcome, index) => {
      if (!outcome.response_details) {
        return null
      }

      const relevantAnswers = outcome.response_details.filter((answer) => {
        return answer.groups && answer.groups.some((group: string) => group === assessmentType)
      })

      if (relevantAnswers.length === 0) {
        return null
      }

      const score = (outcome.scores as any)[assessmentType]
      const dateFormatted = format(parseISO(outcome.response_date), 'MM/dd/yy')

      const getAnswerDescription = () => {
        switch (assessmentType) {
          case 'PSS':
          case 'WELL-BEING':
            return 'Over the last week, how often have you experienced the following?'
          case 'TFM':
          case 'TLFB-ALCOHOL':
            return 'This past week, how many drinks did you consume on each day?'
          case 'WSAS':
            return 'How we feel due to our mental health challenges (e.g., anxiety, depression, stress, sleep) can impact our day-to-day functioning. Please read each statement and rate how much your mental health challenges have impaired your ability to do things in the past week.'
          case 'PCL5':
            return 'Below is a list of problems that people sometimes have in response to a very stressful experience. Please read each problem carefully and then select the appropriate answer to indicate how much you have been bothered by that problem in the past month.'
          case 'PARENTAL-STRESS':
            return 'The following statements describe feelings and perceptions about the experience of being a parent. Think of each of the items in terms of how your relationship with your child or children typically is. Please indicate the degree to which you agree or disagree with the following items by selecting the appropriate response.'
          case 'TLFB-OTHER-SUBSTANCES':
          case 'BDD':
          case 'PARENTING-TASK':
          case 'CGSQ':
          case 'LEAPS':
            return ''
          case 'RCADS-ANXIETY':
          case 'RCADS-DEPRESSION':
          case 'RCADS-TOTAL':
            return 'Please select the option that shows how often each of these things happen to you. There are no right or wrong answers.'
          default:
            return (
              assessment?.answerInstructions ??
              'Over the last week, how often have you been bothered by any of the following problems?'
            )
        }
      }

      return (
        <View key={`answers-${index}`} style={styles.answerSection}>
          <Text style={styles.answerSectionTitle}>Date Submitted: {dateFormatted}</Text>
          <Text style={styles.answerSectionTitle}>
            Final Score: {score} {outcomeUtils.convertOutcomeScoreToDescription(assessmentType, score)}
          </Text>
          <Text style={styles.answerDescription}>{getAnswerDescription()}</Text>
          {relevantAnswers.map((answer, answerIndex) => (
            <View key={answerIndex} style={styles.questionAnswer}>
              <Text style={styles.questionText}>
                {answerIndex + 1} - {(answer as any)['question-text'] || answer.question || 'Question'}
              </Text>
              <Text style={styles.answerText}>
                {(answer as any)['answer-text'] || (answer as any).answer || answer.score || 'No answer'}
              </Text>
            </View>
          ))}
          {renderAnswerDescriptions(assessment)}
        </View>
      )
    })
  }

  const renderAnswerDescriptions = (assessment: any) => {
    const hasIndividualAnswers = assessment?.individualAnswerDescriptions && assessment?.answers
    const hasFinalScoreDescriptions = assessment?.finalScoreDescriptions && assessment?.ranges

    if (!hasIndividualAnswers && !hasFinalScoreDescriptions) {
      return null
    }

    return (
      <View style={styles.scoreKey} wrap={false}>
        {hasIndividualAnswers && (
          <View style={[styles.scoreKeyColumn, hasFinalScoreDescriptions && styles.scoreKeyLeftColumn]}>
            <Text style={styles.scoreKeyTitle}>Individual Answers Range</Text>
            {assessment.answers.map((answer: any, index: number) => (
              <View key={index} style={styles.scoreKeyItem}>
                <Text style={styles.scoreKeyValue}>{answer.value}</Text>
                <Text style={styles.scoreKeyLabel}>{answer.label}</Text>
              </View>
            ))}
          </View>
        )}

        {hasFinalScoreDescriptions && (
          <View style={[styles.scoreKeyColumn, styles.scoreKeyRightColumn]}>
            <Text style={styles.scoreKeyTitle}>Final Score Range</Text>
            {assessment.ranges.map((range: any, index: number) => (
              <View key={index} style={styles.scoreKeyItem}>
                <Text style={styles.scoreKeyValue}>
                  {range.lower}
                  {range.upper ? `-${range.upper}` : '+'}
                </Text>
                <Text style={styles.scoreKeyLabel}>{range.description}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    )
  }

  const outcomesByEpisode: { text: string; id: string; outcomesByEpisode: Outcome[] }[] | undefined =
    providerEpisodesInfo
      ? providerEpisodesInfo.map((episodeInfo) => ({
          ...episodeInfo,
          outcomesByEpisode: outcomesInDateRange.filter((outcome) => outcome.episode_id === episodeInfo.id),
        }))
      : undefined

  const renderedProgressForEpisodes: {
    episodeName: string
    progressByEpisode: React.ReactNode
  }[] = []

  if (outcomesByEpisode) {
    outcomesByEpisode.forEach((episode) => {
      renderedProgressForEpisodes.push({
        episodeName: episode.text,
        progressByEpisode:
          renderProgressTables(episode.outcomesByEpisode) || renderOutcomeCharts(episode.outcomesByEpisode) ? (
            <View>
              {renderProgressTables(episode.outcomesByEpisode)}
              {renderOutcomeCharts(episode.outcomesByEpisode)}
            </View>
          ) : null,
      })
    })
  }

  return renderedProgressForEpisodes
}

export default ClientProgressPDF
